import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { auth } from '@/features/auth/firebase';
import { AuthState } from 'src/constants/auth';
import EmailVerification from 'src/features/auth/EmailVerification';
import MfaVerification from 'src/features/auth/MfaVerification';
import UnauthorizedPage from 'src/features/auth/UnauthorizedPage';
import useAuth from 'src/hooks/useAuth';
import { useTenantFeature } from 'src/hooks/useTenantFeature';
import { useIsFreemiumUser } from 'src/hooks/useIsFreemiumUser';
import { loginRedirectTo, normalizeTenantRedirectPath, resolveTenantCode, toTenantPath } from 'src/lib/tenantRouting';
import type { TenantMembershipRole } from 'src/types/auth';
import type { PlatformFeatureKey } from 'src/types/platformFeatures';

// Handles the auth states shared by every guard: loading, email verification, MFA.
// Returns a blocking element when the caller should not proceed, or null when it should.
// While auth is restoring, keep the current route mounted so a refresh does not
// bounce through the login page.
function useAuthGuardState(): React.ReactElement | null {
  const { authState, isInitialized, completeMfa } = useAuth();

  // Guest routes (the login page) must not paint while the session is still
  // restoring. App routes keep their outlet so a refresh stays put.
  if (!isInitialized) {
    const { pathname } = window.location;
    if (pathname === '/auth' || pathname.startsWith('/auth/')) return null;
    return <Outlet />;
  }
  if (authState === AuthState.EMAIL_UNVERIFIED) return <EmailVerification />;
  if (authState === AuthState.MFA_REQUIRED) return <MfaVerification onVerified={completeMfa} />;

  return null;
}

// Redirects authenticated users to their home page. Used to wrap /auth/login etc.
export function GuestGuard() {
  const block = useAuthGuardState();
  const { isAuthenticated, isInitialized, user } = useAuth();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const tenantCode = resolveTenantCode(pathname, user?.tenants) || null;
  const redirect = normalizeTenantRedirectPath(searchParams.get('redirect') || '/home', tenantCode);
  const isSSO = pathname === '/auth/login-sso';

  if (block) return block;

  if (isAuthenticated && !isSSO) {
    if (user?.tenants?.length === 0) return <Navigate to="/no-tenant" replace />;
    return <Navigate to={redirect} replace />;
  }

  if (!isInitialized && auth.currentUser) return null;

  return <Outlet />;
}

// Requires the user to be authenticated. Used for routes that don't need a specific tenant role.
export function AuthenticatedUserGuard() {
  const block = useAuthGuardState();
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (block) return block;
  // Firebase can already hold the saved session while the profile request is
  // still in flight. Sending that gap to the login page flashes it on a new tab.
  if (!isAuthenticated) {
    if (auth.currentUser) return null;
    return <Navigate to={loginRedirectTo(location)} replace />;
  }

  return <Outlet />;
}

// Requires the user to be a platform admin.
export function PlatformAdminGuard() {
  const block = useAuthGuardState();
  const { isAuthenticated, isPlatformAdmin } = useAuth();
  const location = useLocation();

  if (block) return block;
  if (!isAuthenticated) {
    if (auth.currentUser) return null;
    return <Navigate to={loginRedirectTo(location)} replace />;
  }
  if (!isPlatformAdmin) return <UnauthorizedPage />;

  return <Outlet />;
}

// Requires the user to hold one of the given tenant membership roles.
export function TenantRoleGuard({ accessibleRoles }: { accessibleRoles: TenantMembershipRole[] }) {
  const block = useAuthGuardState();
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const { pathname } = location;
  const tenantRole: TenantMembershipRole | undefined = user?.getActiveTenantRole(pathname);

  if (block) return block;
  if (!isAuthenticated) {
    if (auth.currentUser) return null;
    return <Navigate to={loginRedirectTo(location)} replace />;
  }
  if (user?.tenants?.length === 0) return <Navigate to="/no-tenant" replace />;
  if (!tenantRole || !accessibleRoles.includes(tenantRole)) return <UnauthorizedPage />;

  return <Outlet />;
}

// Blocks freemium users from premium routes, redirecting them to home.
export function PremiumTenantGuard() {
  const block = useAuthGuardState();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isFreemiumUser = useIsFreemiumUser();
  const tenantCode = resolveTenantCode(pathname, user?.tenants);

  if (block) return block;
  if (isFreemiumUser) {
    return <Navigate to={`/${tenantCode}/home`} replace />;
  }

  return <Outlet />;
}

// Requires champion+ role on an organisation tenant with a non-freemium subscription.
export function OrgDashboardGuard({ accessibleRoles }: { accessibleRoles: TenantMembershipRole[] }) {
  const block = useAuthGuardState();
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const { pathname } = location;
  const activeTenant = user?.getActiveTenant(pathname);
  const tenantRole: TenantMembershipRole | undefined = activeTenant?.role as TenantMembershipRole | undefined;

  if (block) return block;
  if (!isAuthenticated) {
    if (auth.currentUser) return null;
    return <Navigate to={loginRedirectTo(location)} replace />;
  }
  if (user?.tenants?.length === 0) return <Navigate to="/no-tenant" replace />;
  if (
    !tenantRole ||
    !accessibleRoles.includes(tenantRole) ||
    activeTenant?.tenant_type !== 'organisation' ||
    activeTenant?.subscription_tier === 'freemium'
  )
    return <UnauthorizedPage />;

  return <Outlet />;
}

// Requires the active tenant's subscription (or add-ons) to include a platform feature.
export function TenantFeatureGuard({
  featureKey,
  redirectTo = '/home'
}: {
  featureKey: PlatformFeatureKey;
  redirectTo?: string;
}) {
  const block = useAuthGuardState();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const hasFeature = useTenantFeature(featureKey);

  if (block) return block;

  if (!hasFeature) {
    const tenantCode = resolveTenantCode(pathname, user?.tenants);
    return <Navigate to={toTenantPath(redirectTo, tenantCode)} replace />;
  }

  return <Outlet />;
}
